import { useEffect, useMemo, useState, type CSSProperties } from "react";
import { useNavigate, useOutletContext } from "react-router-dom";
import {
  adminCreateDraftFromIssue,
  adminListIssues,
  adminResolveIssue,
  type IssueRow,
  type IssueStatus,
} from "../../lib/api";
import type { SessionUser } from "../../types/auth";
import OperationsStudioTabs from "../../components/admin/OperationsStudioTabs";

/**
 * Admin Issue Reports queue.
 *
 * - Status filter pills (Open / In Review / Resolved / Dismissed / All)
 * - Per-issue actions: Mark In Review, Resolve, Dismiss, Re-open
 * - Resolution-note textarea + save (terminal-status writes go through resolve-issue)
 * - "Create Draft From Issue" — clones the affected approved version into a
 *   new draft, links the issue → draft, pre-fills change_summary with the
 *   bartender's note, routes to the editor.
 *
 * Auth: bar_manager+. Anchor `#issue-{id}` jumps the page to a specific issue
 * (used by the Twilio SMS deep link).
 */

const STATUS_FILTERS: { value: IssueStatus | "all"; label: string }[] = [
  { value: "Open", label: "Open" },
  { value: "In Review", label: "In Review" },
  { value: "Resolved", label: "Resolved" },
  { value: "Dismissed", label: "Dismissed" },
  { value: "all", label: "All" },
];

const STATUS_TONE: Record<IssueStatus, "red" | "blue" | "approved" | "muted"> = {
  Open: "red",
  "In Review": "blue",
  Resolved: "approved",
  Dismissed: "muted",
};

export default function AdminIssues() {
  useOutletContext<{ user: SessionUser }>(); // gate (bar_manager+ via AdminLayout)
  const navigate = useNavigate();

  const [statusFilter, setStatusFilter] = useState<IssueStatus | "all">("Open");
  const [issues, setIssues] = useState<IssueRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [busyId, setBusyId] = useState<string | null>(null);
  const [flash, setFlash] = useState<string | null>(null);

  function showFlash(msg: string) {
    setFlash(msg);
    setTimeout(() => setFlash(null), 3000);
  }

  async function reload() {
    setLoading(true);
    setError(null);
    try {
      const r = await adminListIssues(statusFilter);
      setIssues(r.issues);
    } catch (err: any) {
      setError(err?.message ?? "Could not load issues");
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    reload();
  }, [statusFilter]);

  // Twilio SMS deep link: /admin/issues#{id} — scroll the matching card into view.
  useEffect(() => {
    if (!loading && issues.length > 0 && window.location.hash) {
      const target = document.getElementById(window.location.hash.slice(1));
      if (target) target.scrollIntoView({ behavior: "smooth", block: "center" });
    }
  }, [loading, issues]);

  async function setStatus(issue: IssueRow, status: IssueStatus, resolutionNote?: string) {
    setBusyId(issue.id);
    try {
      await adminResolveIssue(issue.id, status, resolutionNote);
      showFlash(`Status: ${status}`);
      reload();
    } catch (err: any) {
      setError(err?.message ?? "Could not update");
    } finally {
      setBusyId(null);
    }
  }

  async function createDraft(issue: IssueRow) {
    setBusyId(issue.id);
    try {
      const r = await adminCreateDraftFromIssue(issue.id);
      if (r.slug && r.version_id) {
        navigate(`/admin/recipes/${r.slug}/edit/${r.version_id}`);
      } else {
        showFlash(r.already_existed ? "Draft already exists" : "Draft created");
        reload();
      }
    } catch (err: any) {
      setError(err?.message ?? "Could not create draft");
    } finally {
      setBusyId(null);
    }
  }

  const counts = useMemo(() => {
    const c: Record<string, number> = { Open: 0, "In Review": 0, Resolved: 0, Dismissed: 0 };
    for (const i of issues) c[i.status] = (c[i.status] ?? 0) + 1;
    return c;
  }, [issues]);

  return (
    <section style={styles.shell}>
      <OperationsStudioTabs activeTab="issues" badgeCounts={{ issues: counts.Open }} />
      <p style={styles.eyebrow}>Service workflow</p>
      <h1 style={styles.title}>Issue reports</h1>
      <p style={styles.body}>
        Bartender-submitted recipe issues. Status changes here notify the
        bartender on their next refresh; "Create Draft From Issue" clones the
        affected version + pre-fills the change_summary so you can edit, test,
        and approve a fix.
      </p>

      {/* Toggle-button group, NOT a tablist — these are mutually-exclusive
          filter pills that don't need ARIA tab semantics + arrow-key
          navigation. aria-pressed marks the active filter. */}
      <div style={styles.filterRow} role="group" aria-label="Issue status filter">
        {STATUS_FILTERS.map((f) => {
          const active = statusFilter === f.value;
          return (
            <button
              key={f.value}
              type="button"
              aria-pressed={active}
              onClick={() => setStatusFilter(f.value)}
              style={active ? styles.pillActive : styles.pill}
            >
              {f.label}
              {f.value !== "all" && counts[f.value] > 0 && (
                <span style={styles.pillCount}>{counts[f.value]}</span>
              )}
            </button>
          );
        })}
      </div>

      {flash && <div style={styles.flash} role="status">{flash}</div>}
      {error && <div style={styles.errorBox} role="alert">{error}</div>}
      {loading && <p style={styles.muted}>Loading issues…</p>}
      {!loading && issues.length === 0 && (
        <p style={styles.muted}>
          {statusFilter === "Open"
            ? "🍸 No open issues. (Quiet service.)"
            : "No issues match this filter."}
        </p>
      )}

      <ol style={styles.list}>
        {issues.map((issue) => (
          <IssueCard
            key={issue.id}
            issue={issue}
            busy={busyId === issue.id}
            onStatusChange={(s, note) => setStatus(issue, s, note)}
            onCreateDraft={() => createDraft(issue)}
          />
        ))}
      </ol>
    </section>
  );
}

interface IssueCardProps {
  issue: IssueRow;
  busy: boolean;
  onStatusChange: (status: IssueStatus, resolutionNote?: string) => void;
  onCreateDraft: () => void;
}

function IssueCard({ issue, busy, onStatusChange, onCreateDraft }: IssueCardProps) {
  const [resolutionNote, setResolutionNote] = useState(issue.resolution_note ?? "");
  const tone = STATUS_TONE[issue.status];
  const isTerminal = issue.status === "Resolved" || issue.status === "Dismissed";

  return (
    <li id={`issue-${issue.id}`} style={styles.card}>
      <div style={styles.cardTop}>
        <div style={styles.cardTopLeft}>
          <span style={{ ...styles.statusChip, ...statusChipTone[tone] }}>{issue.status}</span>
          <span style={styles.typeChip}>{issue.issue_type}</span>
          {issue.draft_version_id && (
            <span style={styles.draftChip}>Draft created</span>
          )}
        </div>
        <span style={styles.timestamp}>{new Date(issue.created_at).toLocaleString()}</span>
      </div>

      <div style={styles.recipeLine}>
        <strong>{issue.drink_name}</strong>
        {issue.version_number && <span style={styles.subMeta}> · v{issue.version_number}</span>}
        {issue.drink_service_status && (
          <span style={styles.subMeta}> · {issue.drink_service_status}</span>
        )}
      </div>

      <p style={styles.note}>{issue.note}</p>

      <p style={styles.metaLine}>
        Submitted by{" "}
        <strong>
          {issue.station_label ?? issue.submitted_by_name ?? "(unknown)"}
        </strong>
        {issue.submitted_by_name && issue.station_label !== issue.submitted_by_name && (
          <span style={styles.subMeta}> ({issue.submitted_by_name})</span>
        )}
        {issue.notification_sent_at && (
          <span style={styles.subMeta}> · SMS sent</span>
        )}
      </p>

      {(isTerminal || issue.resolution_note) && (
        <div style={styles.resolutionBlock}>
          <label style={styles.resolutionLabel}>
            Resolution note
            {issue.resolved_by_name && (
              <span style={styles.subMeta}>
                {" "}· by {issue.resolved_by_name}
                {issue.resolved_at && ` · ${new Date(issue.resolved_at).toLocaleString()}`}
              </span>
            )}
          </label>
          <textarea
            value={resolutionNote}
            onChange={(e) => setResolutionNote(e.target.value)}
            placeholder="What did you change / decide?"
            style={styles.resolutionInput}
            rows={2}
            disabled={busy}
          />
        </div>
      )}

      <div style={styles.actionsRow}>
        {issue.status !== "In Review" && !isTerminal && (
          <button
            type="button"
            onClick={() => onStatusChange("In Review")}
            disabled={busy}
            style={styles.btnSecondary}
          >
            Mark In Review
          </button>
        )}
        {!isTerminal && (
          <>
            <button
              type="button"
              onClick={() => onStatusChange("Resolved", resolutionNote.trim() || undefined)}
              disabled={busy}
              style={styles.btnGreen}
            >
              Resolve
            </button>
            <button
              type="button"
              onClick={() => onStatusChange("Dismissed", resolutionNote.trim() || undefined)}
              disabled={busy}
              style={styles.btnRed}
            >
              Dismiss
            </button>
          </>
        )}
        {isTerminal && (
          <button
            type="button"
            onClick={() => onStatusChange("Open")}
            disabled={busy}
            style={styles.btnSecondary}
          >
            Re-open
          </button>
        )}
        <button
          type="button"
          onClick={onCreateDraft}
          disabled={busy}
          style={styles.btnGold}
          title={
            issue.draft_version_id
              ? "A draft already exists for this issue — opens the editor"
              : "Clone the affected approved version into a new draft + pre-fill change summary"
          }
        >
          {issue.draft_version_id ? "Open draft" : "Create Draft From Issue"}
        </button>
      </div>
    </li>
  );
}

const statusChipTone: Record<string, CSSProperties> = {
  red: { background: "rgba(224,80,64,0.16)", color: "#FF8A7A", borderColor: "rgba(224,80,64,0.4)" },
  blue: { background: "rgba(96,165,250,0.16)", color: "#93c5fd", borderColor: "rgba(96,165,250,0.3)" },
  approved: { background: "rgba(39,174,96,0.16)", color: "#86efac", borderColor: "rgba(39,174,96,0.3)" },
  muted: { background: "var(--bg)", color: "var(--muted)", borderColor: "var(--border)" },
};

const styles: Record<string, CSSProperties> = {
  shell: { maxWidth: 900, padding: "8px 0 32px" },
  eyebrow: {
    margin: 0,
    fontSize: 11,
    fontWeight: 800,
    letterSpacing: 0.6,
    textTransform: "uppercase",
    color: "var(--gold)",
  },
  title: {
    fontFamily: "Georgia, serif",
    fontSize: 28,
    margin: "8px 0 12px",
    letterSpacing: -0.4,
    color: "var(--text)",
  },
  body: { color: "var(--muted)", fontSize: 13, lineHeight: 1.5, margin: "0 0 16px" },
  filterRow: { display: "flex", gap: 6, marginBottom: 16, flexWrap: "wrap" },
  pill: {
    background: "var(--bg)",
    color: "var(--muted)",
    border: "1px solid var(--border)",
    borderRadius: 999,
    padding: "8px 14px",
    fontSize: 12,
    fontWeight: 700,
    letterSpacing: 0.4,
    textTransform: "uppercase",
    cursor: "pointer",
    minHeight: 40,
    display: "inline-flex",
    alignItems: "center",
    gap: 6,
  },
  pillActive: {
    background: "var(--gold)",
    color: "#0D0B08",
    border: "1px solid var(--gold)",
    borderRadius: 999,
    padding: "8px 14px",
    fontSize: 12,
    fontWeight: 800,
    letterSpacing: 0.4,
    textTransform: "uppercase",
    cursor: "pointer",
    minHeight: 40,
    display: "inline-flex",
    alignItems: "center",
    gap: 6,
  },
  pillCount: {
    background: "rgba(0,0,0,0.18)",
    borderRadius: 999,
    padding: "1px 6px",
    fontSize: 10,
    fontWeight: 800,
  },
  flash: {
    margin: "0 0 12px",
    padding: 10,
    background: "rgba(39,174,96,0.12)",
    border: "1px solid rgba(39,174,96,0.4)",
    color: "#86efac",
    borderRadius: 10,
    fontSize: 12,
    fontWeight: 700,
  },
  errorBox: {
    margin: "0 0 12px",
    padding: 10,
    background: "rgba(224,80,64,0.1)",
    border: "1px solid rgba(224,80,64,0.4)",
    color: "#FF8A7A",
    borderRadius: 10,
    fontSize: 12,
  },
  muted: { color: "var(--muted)", fontSize: 13, padding: 8 },
  list: { listStyle: "none", padding: 0, margin: 0, display: "grid", gap: 12 },
  card: {
    background: "var(--surface)",
    border: "1px solid var(--border)",
    borderRadius: 14,
    padding: 14,
    display: "grid",
    gap: 10,
  },
  cardTop: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: 8,
    flexWrap: "wrap",
  },
  cardTopLeft: { display: "flex", gap: 6, flexWrap: "wrap", alignItems: "center" },
  statusChip: {
    display: "inline-flex",
    alignItems: "center",
    padding: "3px 10px",
    borderRadius: 999,
    border: "1px solid",
    fontSize: 10,
    fontWeight: 800,
    letterSpacing: 0.4,
    textTransform: "uppercase",
  },
  typeChip: {
    fontSize: 10,
    fontWeight: 800,
    letterSpacing: 0.4,
    textTransform: "uppercase",
    color: "var(--muted)",
    background: "var(--bg)",
    border: "1px solid var(--border)",
    borderRadius: 999,
    padding: "3px 8px",
  },
  draftChip: {
    fontSize: 10,
    fontWeight: 800,
    letterSpacing: 0.4,
    textTransform: "uppercase",
    color: "var(--gold)",
    background: "rgba(249,175,54,0.12)",
    border: "1px solid rgba(249,175,54,0.3)",
    borderRadius: 999,
    padding: "3px 8px",
  },
  timestamp: { fontSize: 11, color: "var(--muted)", fontWeight: 600 },
  recipeLine: { fontSize: 14, color: "var(--text)" },
  subMeta: {
    color: "var(--muted)",
    fontFamily: "ui-monospace, SFMono-Regular, Menlo, monospace",
    fontSize: 11,
  },
  note: {
    margin: 0,
    fontSize: 14,
    lineHeight: 1.45,
    color: "var(--text)",
    background: "var(--bg)",
    border: "1px solid var(--border)",
    borderRadius: 10,
    padding: 10,
    whiteSpace: "pre-wrap",
  },
  metaLine: { margin: 0, fontSize: 12, color: "var(--muted)", lineHeight: 1.4 },
  resolutionBlock: { display: "grid", gap: 4 },
  resolutionLabel: {
    fontSize: 11,
    fontWeight: 800,
    letterSpacing: 0.4,
    textTransform: "uppercase",
    color: "var(--muted)",
  },
  resolutionInput: {
    width: "100%",
    boxSizing: "border-box",
    background: "var(--bg)",
    color: "var(--text)",
    border: "1px solid var(--border)",
    borderRadius: 10,
    padding: 10,
    fontSize: 13,
    fontFamily: "inherit",
    resize: "vertical",
  },
  actionsRow: {
    display: "flex",
    gap: 8,
    flexWrap: "wrap",
  },
  btnSecondary: {
    background: "transparent",
    color: "var(--text)",
    border: "1px solid var(--border)",
    borderRadius: 8,
    padding: "8px 14px",
    fontSize: 12,
    fontWeight: 700,
    cursor: "pointer",
    minHeight: 40,
  },
  btnGreen: {
    background: "rgba(39,174,96,0.18)",
    color: "#86efac",
    border: "1px solid rgba(39,174,96,0.4)",
    borderRadius: 8,
    padding: "8px 14px",
    fontSize: 12,
    fontWeight: 700,
    cursor: "pointer",
    minHeight: 40,
  },
  btnRed: {
    background: "rgba(224,80,64,0.16)",
    color: "#FF8A7A",
    border: "1px solid rgba(224,80,64,0.4)",
    borderRadius: 8,
    padding: "8px 14px",
    fontSize: 12,
    fontWeight: 700,
    cursor: "pointer",
    minHeight: 40,
  },
  btnGold: {
    background: "var(--gold)",
    color: "#0D0B08",
    border: 0,
    borderRadius: 8,
    padding: "8px 14px",
    fontSize: 12,
    fontWeight: 800,
    cursor: "pointer",
    minHeight: 40,
  },
};
